import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { orderType as parseOrderType } from '@/constants';
import { useUser } from '@/contexts/UserContext';
import DrawerSubMenu from '@/leftDrawer/DrawerSubMenu';
import MenuIcon from '@/leftDrawer/MenuIcon';
import SearchResultsIcon from '@/svg-icons/inspector';
import { cn } from '@/lib/utils';

function DrawerMenu({
  menu,
  handleSubMenuChange,
  handleMenuChange,
  handleCollapseSubMenuChange,
  isDisplayingSearchResults,
  subMenuToOpenByIndex,
}) {
  const me = useUser();
  const divergenceCount = useSelector(state => state?.divergences?.count);

  const features = me?.features?.map(feature => parseOrderType.get(feature));

  return (
    <div className="mt-2.5 flex flex-1 flex-col justify-between text-base font-bold">
      <div>
        {menu
          ?.filter(menuOption => menuOption.active)
          .map((menuOption, mIndex) => {
            return (
              <div key={mIndex} className="mb-2">
                <div
                  className="relative flex select-none items-center h-[48px] pl-[18px] pr-4 text-[#556B95] hover:bg-[#162A42] hover:text-white"
                  data-cy="nav-item-parent"
                >
                  <div className="min-w-[48px] shrink-0 opacity-90">
                    <MenuIcon menuKey={menuOption.key} />
                  </div>
                  <span className="tracking-wide">
                    <FormattedMessage id={`LeftDrawer.${menuOption.key}`} />
                  </span>
                  <SearchResultsIcon
                    className={cn(
                      'absolute right-5 text-xl transition-all duration-300',
                      isDisplayingSearchResults ? 'opacity-100 visible' : 'opacity-0 invisible'
                    )}
                  />
                </div>

                <DrawerSubMenu
                  subMenu={menuOption.subMenu}
                  handleSubMenuChange={handleSubMenuChange}
                  handleCollapseSubMenuChange={handleCollapseSubMenuChange}
                  subMenuToOpenByIndex={subMenuToOpenByIndex}
                />
              </div>
            );
          })}
      </div>

      <div className="w-full">
        {menu
          ?.filter(menuOption => !menuOption.active)
          ?.filter(menuOption => !menuOption.requiredFeature || features?.indexOf(menuOption?.requiredFeature) >= 0)
          .map((menuOption, mIndex) => (
            <div
              className="left-drawer-secondary-item"
              key={mIndex}
              onClick={e => {
                if (menuOption?.onClick && typeof menuOption?.onClick === 'function') {
                  menuOption.onClick();
                } else {
                  handleMenuChange(mIndex, e, menuOption.link, menuOption.routeLink);
                }
              }}
            >
              {menuOption.key === 'divergences' && !!divergenceCount && (
                <span className="absolute top-[3px] left-[6px] w-[20px] h-[20px] flex items-center justify-center rounded-full bg-[#9e3c5b] text-[12px] font-normal leading-none text-white pointer-events-none z-[10]">
                  {divergenceCount}
                </span>
              )}

              <div className="left-drawer-secondary-icon shrink-0 leading-[0]">
                <MenuIcon menuKey={menuOption.key} />
              </div>

              <span className="tracking-wide">
                <FormattedMessage id={`LeftDrawer.${menuOption.key}`} />
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}

DrawerMenu.propTypes = {
  /**
   * Array with the menu options
   */
  menu: PropTypes.array.isRequired,
  /**
   * Function to handle the change of the menu
   */
  handleMenuChange: PropTypes.func.isRequired,
  /**
   * Function to handle the change of the sub-menu
   */
  handleSubMenuChange: PropTypes.func.isRequired,
  /**
   * Function to handle the url change of the collapse-menu (submenu inside submenu)
   */
  handleCollapseSubMenuChange: PropTypes.func.isRequired,
  /**
   * Is displaying search results list
   */
  isDisplayingSearchResults: PropTypes.bool,
  /**
   * Default submenu index to open (if it is provided)
   */
  subMenuToOpenByIndex: PropTypes.number,
};

DrawerMenu.defaultProps = {
  isDisplayingSearchResults: false,
  subMenuToOpenByIndex: null,
};

export default DrawerMenu;
